import React from "react";
import {
  Heading,
  Flex,
  Button,
  IconButton,
  Box,
  Stack,
  Link,
  Tag,
  TagLabel,
  Image,
  Icon,
} from "@chakra-ui/react";
import { FiEdit2, FiPlus } from "react-icons/fi";
import { RiDeleteBin5Line, RiQrCodeLine } from "react-icons/ri";
import { BsCreditCard2Back, BsCardImage } from "react-icons/bs";
import { PaymentMethod } from "../../fetchers/model";
import { useNavigate } from "react-router-dom";
import ConfirmationDialog from "../../utils/Confirmation";
import { useTranslation } from "react-i18next";
import { Table as AITable } from "../../utils/Table";
import axios from "axios";
import { Select } from "chakra-react-select";
import { FilterValue, SortBy } from "../../utils/CommonInterface";
import API from "../../config/api";

export default function ListPaymentMethod() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = React.useState<boolean>(true);
  const [data, setData] = React.useState<PaymentMethod[]>([]);
  const [pageCount, setPageCount] = React.useState(0);
  const fetchIdRef = React.useRef(0);

  const fetchData = React.useCallback(
    async ({
      pageSize,
      pageIndex,
      globalFilter,
      filters,
      sortBy,
    }: {
      pageSize: number;
      pageIndex: number;
      globalFilter: string;
      filters: FilterValue[];
      sortBy: SortBy[];
    }) => {
      const fetchId = ++fetchIdRef.current;

      setLoading(true);

      const typeFilters = filters.filter(
        (x) => x.id === "publisher" && x.value.length > 0
      );
      let typeParams =
        (typeFilters.length > 0 &&
          typeFilters[0].value.map((x) => `&payment_type[]=${x}`).join("")) ||
        "";

      const statusFilters = filters.filter(
        (x) => x.id === "status" && x.value.length > 0
      );
      let statusParams =
        (statusFilters.length > 0 &&
          statusFilters[0].value.map((x) => `&status[]=${x}`).join("")) ||
        "";

      let sortByParam =
        (sortBy.length > 0 &&
          `&order=${sortBy[0].desc ? "-" : ""}${sortBy[0].id}`) ||
        "";

      const { data, headers } = await axios.get<PaymentMethod[]>(
        `${API.PAYMENT_METHOD}?page=${pageIndex + 1}&per_page=${pageSize}${
          globalFilter ? `&keyword=${globalFilter}` : ""
        }${typeParams}${statusParams}${sortByParam}`
      );
      if (fetchId === fetchIdRef.current) {
        setData(data);
        setPageCount(Math.ceil(parseInt(headers["total"]) / pageSize));

        setLoading(false);
      }
    },
    []
  );

  const typeOptions = ["credit_card", "qr_code"].map(function (val) {
    return {
      label: t(`payment_method.payment_type.${val}`),
      value: val,
    };
  });

  const statusOptions = ["activate", "deactivate"].map(function (val) {
    return {
      label: t(`payment_method.status.${val}`),
      value: val,
    };
  });

  const tableColumns = [
    {
      Header: t("payment_method.name"),
      accessor: "name" as const,
      disableFilters: true,
    },
    {
      Header: t("payment_method.publisher"),
      FilterHeader: t("payment_method.payment_type.name"),
      accessor: "publisher" as const,
      Filter: ({ column }: { column: any }) => (
        <Select
          size="sm"
          isMulti
          name={column.id}
          closeMenuOnSelect={false}
          value={typeOptions.filter(
            (x) => column.filterValue && column.filterValue.includes(x.value)
          )}
          options={typeOptions}
          onChange={(option) => column.setFilter(option.map((x) => x.value))}
        />
      ),
      filterable: true,
    },
    {
      Header: t("payment_method.refund_money"),
      accessor: "refund_money" as const,
      disableFilters: true,
    },
    {
      Header: t("payment_method.url"),
      accessor: "url" as const,
      disableFilters: true,
    },
    {
      Header: t("payment_method.status.title"),
      accessor: "status" as const,
      FilterHeader: t("payment_method.status.title"),
      Filter: ({ column }: { column: any }) => (
        <Select
          size="sm"
          isMulti
          name={column.id}
          closeMenuOnSelect={false}
          value={statusOptions.filter(
            (x) => column.filterValue && column.filterValue.includes(x.value)
          )}
          options={statusOptions}
          onChange={(option) => column.setFilter(option.map((x) => x.value))}
        />
      ),
      filterable: true,
    },
    {
      Header: "",
      accessor: "action" as const,
      disableFilters: true,
      disableSortBy: true,
    },
  ];

  const tableData = data.map((item) => ({
    name: (
      <Stack direction="row" spacing={2} alignItems="center">
        {item.logo && item.logo.id ? (
          <Image
            boxSize="36px"
            objectFit="cover"
            src={item.logo.url}
            alt={item.name}
          />
        ) : (
          <Icon as={BsCardImage} boxSize="36px" color={"gray.400"} />
        )}
        <Link
          color="blue.500"
          href={`/console/payment_method/${item.id}`}
          fontWeight={"bold"}
        >
          {item.name}
        </Link>
      </Stack>
    ),
    publisher: (
      <Stack direction={"row"} spacing={2} alignItems={"center"}>
        {item.payment_type === "credit_card" ? (
          <BsCreditCard2Back color="blue" />
        ) : (
          <RiQrCodeLine color="blue" />
        )}
        <Box>{item.publisher}</Box>
      </Stack>
    ),
    refund_money:
      item.refund_type === "percentage"
        ? `${item.refund_money * 100}%`
        : item.refund_money,
    url: item.url,
    status: (
      <Tag
        size={"sm"}
        borderRadius="full"
        variant="solid"
        colorScheme={item.status === "activate" ? "blue" : "gray"}
      >
        <TagLabel>{t(`payment_method.status.${item.status}`)}</TagLabel>
      </Tag>
    ),
    action: (
      <Stack direction={"row"} spacing={1} alignItems={"center"}>
        <IconButton
          icon={<FiEdit2 />}
          aria-label={t("action.edit")}
          variant="link"
          onClick={() => navigate(`/console/payment_method/${item.id}/edit`)}
        />
        <ConfirmationDialog
          title={t("payment_method.delete.title")}
          description={t("payment_method.delete.description", {
            payment_method_name: item.name,
          })}
          onConfirm={() => deleteItem(item.id)}
        >
          <IconButton
            icon={<RiDeleteBin5Line />}
            aria-label={t("action.delete")}
            variant="link"
          />
        </ConfirmationDialog>
      </Stack>
    ),
  }));

  const deleteItem = async (itemId: string) => {
    await axios.delete<void>(`${API.PAYMENT_METHOD}/${itemId}`);
    setData(data.filter((x) => x.id !== itemId));
  };

  return (
    <>
      <Flex justifyContent="space-between">
        <Heading as="h4" size="lg" mb="4">
          {t("payment_method.title.name")}
        </Heading>
        <Button
          leftIcon={<FiPlus />}
          colorScheme="blue"
          variant="solid"
          onClick={() => navigate("/console/payment_method/new")}
        >
          {t("action.new")}
        </Button>
      </Flex>
      <AITable
        columns={tableColumns}
        data={tableData}
        fetchData={fetchData}
        loading={loading}
        pageCount={pageCount}
        useSearch={true}
        disableFilters={false}
        disableSortBy={false}
      />
    </>
  );
}
